import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { altitudeStopsForMapLibre } from "./altitude-color";
import runwaysData from "./data/runways.json";
import { escapeHtml } from "./escape";
import { extractLatLon } from "./notam-lang";
import {
  $airmet,
  $airport,
  $currentIcao,
  $icaoUserPicked,
  $notams,
  $planes,
  $selectedId,
  $station,
  $theme,
  $trails,
  selectPlane,
} from "./store";
import type { Theme } from "./types";

type MLMap = maplibregl.Map;
type MLMarker = maplibregl.Marker;

// Tile provider URL templates. Overridable per-build via
// VITE_MAP_STYLE_DARK / VITE_MAP_STYLE_LIGHT — point at MapTiler /
// Stadia / a self-hosted planetiler instance if OpenFreeMap becomes
// rate-limited or its terms change.
// See SCALING_AUDIT.md S-16 and docs/DEVELOPMENT.md "Tile provider".
const STYLE_URL: Record<Theme, string> = {
  dark:
    (import.meta.env.VITE_MAP_STYLE_DARK as string | undefined) ||
    "https://tiles.openfreemap.org/styles/dark",
  light:
    (import.meta.env.VITE_MAP_STYLE_LIGHT as string | undefined) ||
    "https://tiles.openfreemap.org/styles/positron",
};

let map: MLMap | null = null;
let stationMarker: MLMarker | null = null;
const planeEls: Map<string, HTMLDivElement> = new Map();
const planeMarkers: Map<string, MLMarker> = new Map();
const airportMarkers: MLMarker[] = [];

export function initMap(containerId: string): MLMap {
  const s = $station.get();
  const m = new maplibregl.Map({
    container: containerId,
    style: STYLE_URL[$theme.get()],
    center: [s.lon, s.lat],
    zoom: 9,
    attributionControl: false,
    pitchWithRotate: false,
    dragRotate: false,
  });
  m.addControl(
    new maplibregl.AttributionControl({
      compact: true,
      // Aircraft DB credit is REQUIRED by ODC-BY 1.0 (the license tar1090-db /
      // Mictronics export ships under). Keep the link clickable so reviewers
      // can verify the license without leaving the page.
      customAttribution: [
        "OpenFreeMap · OpenMapTiles · © OpenStreetMap",
        'Aircraft DB: <a href="https://www.mictronics.de/aircraft-database/" target="_blank" rel="noopener">Mictronics</a> via <a href="https://github.com/wiedehopf/tar1090-db" target="_blank" rel="noopener">tar1090-db</a> · <a href="https://opendatacommons.org/licenses/by/1.0/" target="_blank" rel="noopener">ODC-BY 1.0</a>',
      ].join(" | "),
    }),
    "bottom-right",
  );
  // MapLibre's compact AttributionControl renders EXPANDED on first
  // paint (it only collapses after the user clicks the "i"). Collapse
  // it explicitly so the multi-line credit doesn't squat the bottom of
  // the map on load — the user can always tap "i" to see attribution.
  requestAnimationFrame(() => {
    const attribEl = m.getContainer().querySelector(".maplibregl-ctrl-attrib");
    attribEl?.classList.remove("maplibregl-compact-show");
  });
  m.addControl(
    new maplibregl.NavigationControl({ showCompass: false, visualizePitch: false }),
    "bottom-right",
  );
  m.addControl(
    new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: false,
      showUserLocation: true,
    }),
    "bottom-right",
  );

  map = m;

  m.on("load", () => {
    // Belt-and-suspenders: ensure canvas matches container after first layout
    requestAnimationFrame(() => m.resize());
    drawRunways();
    drawAirportMarkers();
    drawStationMarker();
    drawAirmet();
    drawNotams();
    drawTrail();
    syncPlanes();

    // Try geolocation in the background; recenter only if user grants & we still
    // have the default station.
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          $station.set({ lat: pos.coords.latitude, lon: pos.coords.longitude, source: "geo" });
          m.easeTo({ center: [pos.coords.longitude, pos.coords.latitude], duration: 800, zoom: 10 });
          drawStationMarker();
        },
        () => {
          // permission denied / unavailable — keep default
        },
        { timeout: 5000 },
      );
    }
  });

  // Re-apply on theme toggle
  $theme.subscribe((t) => {
    if (!map) return;
    map.setStyle(STYLE_URL[t]);
    map.once("styledata", () => {
      drawRunways();
      drawAirportMarkers();
      drawStationMarker();
      drawAirmet();
      drawNotams();
      drawTrail();
      syncPlanes();
    });
  });

  $planes.subscribe(() => syncPlanes());
  $selectedId.subscribe(() => {
    updateSelectedClass();
    drawTrail();
  });
  $trails.subscribe(() => drawTrail());
  // When the station first switches off the hard-coded default to a real
  // Pi-reported location, recenter the map so the user lands on their
  // actual receiver instead of staring at the default Setagaya marker.
  // `geo` updates already easeTo'd inside the geolocation callback above;
  // we only do it for `pi` here, and only on the first transition (cur
  // tracked via a closure flag) to avoid yanking the camera around if
  // the Pi later flips lat/lon for any reason.
  let stationCentered = false;
  $station.subscribe((s) => {
    drawStationMarker();
    if (!stationCentered && s.source === "pi" && map) {
      stationCentered = true;
      map.easeTo({ center: [s.lon, s.lat], duration: 800, zoom: 9 });
    }
  });
  // Active-runway highlight depends on $airport — redraw when it changes.
  $airport.subscribe(() => {
    if (map?.isStyleLoaded()) drawRunways();
  });
  // Airmet/NOTAM stores can update before the style is ready. Defer to the
  // next idle frame which guarantees we have a loaded style.
  $airmet.subscribe(() => {
    if (!map) return;
    if (map.isStyleLoaded()) drawAirmet();
    else map.once("idle", () => drawAirmet());
  });
  $notams.subscribe(() => {
    if (!map) return;
    if (map.isStyleLoaded()) drawNotams();
    else map.once("idle", () => drawNotams());
  });

  return m;
}

/** Render the airmet/sigmet polygons as a translucent fill layer + outline. */
function drawAirmet() {
  // Guard: only attempt after the style is fully loaded; otherwise addSource
  // / addLayer throw "Style is not done loading". $airmet.subscribe fires
  // before initMap's "load" callback in some race orders.
  if (!map || !map.isStyleLoaded()) return;
  const fc = $airmet.get() ?? { type: "FeatureCollection" as const, features: [] };
  if (map.getSource("airmet")) {
    (map.getSource("airmet") as maplibregl.GeoJSONSource).setData(fc as never);
    return;
  }
  map.addSource("airmet", { type: "geojson", data: fc as never });

  // Color by hazard. Anything not listed → grey.
  const colorMatch: maplibregl.ExpressionSpecification = [
    "match",
    ["upcase", ["coalesce", ["get", "hazard"], "OTHER"]],
    "TURB",
    "rgb(255,200,0)",
    "ICE",
    "rgb(140,180,255)",
    "ICING",
    "rgb(140,180,255)",
    "IFR",
    "rgb(170,130,220)",
    "MTOS",
    "rgb(180,140,100)",
    "MTN_OBS",
    "rgb(180,140,100)",
    "CONV",
    "rgb(255,90,90)",
    "TS",
    "rgb(255,90,90)",
    "VA",
    "rgb(255,120,40)",
    "rgb(180,180,180)",
  ];
  map.addLayer({
    id: "airmet-fill",
    type: "fill",
    source: "airmet",
    paint: {
      "fill-color": colorMatch,
      "fill-opacity": ["case", ["==", ["get", "kind"], "SIGMET"], 0.28, 0.18],
    },
  });
  // MapLibre doesn't allow data-driven expressions for line-dasharray;
  // it has to be a literal. So we ship two separate outline layers and
  // filter each by kind. SIGMETs get solid, AIRMETs get dashed.
  map.addLayer({
    id: "airmet-outline-sig",
    type: "line",
    source: "airmet",
    filter: ["==", ["get", "kind"], "SIGMET"],
    paint: {
      "line-color": colorMatch,
      "line-width": 2,
      "line-opacity": 0.85,
    },
  });
  map.addLayer({
    id: "airmet-outline-am",
    type: "line",
    source: "airmet",
    filter: ["!=", ["get", "kind"], "SIGMET"],
    paint: {
      "line-color": colorMatch,
      "line-width": 1.2,
      "line-opacity": 0.85,
      "line-dasharray": [4, 3],
    },
  });

  // Popup on click — show hazard / FIR / validity / raw text.
  // EVERY interpolation of upstream-sourced data goes through escapeHtml
  // — a malicious AIRMET payload (compromised upstream feed) injecting
  // `<script>` would otherwise execute in the user's browser the moment
  // they clicked the polygon. See SCALING_AUDIT.md B-07.
  map.on("click", "airmet-fill", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties ?? {};
    const html = `
      <div style="font-family:Barlow,sans-serif;font-size:12px;max-width:260px">
        <div style="font-weight:700">${escapeHtml(p.kind)} · ${escapeHtml(p.hazard)}${p.severity ? " · " + escapeHtml(p.severity) : ""}</div>
        ${p.fir ? `<div style="color:#666;margin-top:2px">FIR ${escapeHtml(p.fir)}</div>` : ""}
        ${p.altLowFt || p.altHighFt ? `<div style="color:#666">FL${escapeHtml(p.altLowFt ?? "—")} → FL${escapeHtml(p.altHighFt ?? "—")}</div>` : ""}
        <div style="color:#666;margin-top:4px">${escapeHtml(p.validFrom ?? "")} → ${escapeHtml(p.validTo ?? "")}</div>
        ${p.raw ? `<pre style="white-space:pre-wrap;font-size:11px;margin:6px 0 0">${escapeHtml(p.raw)}</pre>` : ""}
      </div>`;
    new maplibregl.Popup({ closeOnClick: true, maxWidth: "280px" })
      .setLngLat(e.lngLat)
      .setHTML(html)
      .addTo(map!);
  });
  map.on("mouseenter", "airmet-fill", () => {
    if (map) map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "airmet-fill", () => {
    if (map) map.getCanvas().style.cursor = "";
  });
}

/** Plot point-NOTAMs (those whose body contains a PSN coordinate — mostly
 *  OBST entries) as severity-colored circles. Click opens the NOTAM panel
 *  and emits an event the panel listens for to scroll the row into view.
 *  Polygon/area NOTAMs aren't handled here (we don't get GeoJSON for them
 *  from the API — that would require a follow-up). */
function drawNotams() {
  if (!map || !map.isStyleLoaded()) return;
  const features: GeoJSON.Feature[] = [];
  const rows = $notams.get();
  for (const n of rows) {
    const pos = extractLatLon(n.body ?? "");
    if (!pos) continue;
    features.push({
      type: "Feature",
      properties: {
        id: n.notamId,
        severity: n.severity ?? "info",
        code: n.notamCode ?? "",
        body: (n.body ?? "").slice(0, 240),
      },
      geometry: { type: "Point", coordinates: [pos.lon, pos.lat] },
    });
  }
  const collection: GeoJSON.FeatureCollection = { type: "FeatureCollection", features };
  if (map.getSource("notams")) {
    (map.getSource("notams") as maplibregl.GeoJSONSource).setData(collection);
    return;
  }
  map.addSource("notams", { type: "geojson", data: collection });
  // Outer halo — fainter, larger ring so a single dot is still visible
  // against busy basemap labels at low zoom.
  map.addLayer({
    id: "notam-halo",
    type: "circle",
    source: "notams",
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 6, 12, 11, 16, 18],
      "circle-color": [
        "match",
        ["get", "severity"],
        "crit",
        "rgb(255,100,100)",
        "warn",
        "rgb(255,200,90)",
        "rgba(160,180,210,1)",
      ],
      "circle-opacity": 0.18,
      "circle-stroke-color": [
        "match",
        ["get", "severity"],
        "crit",
        "rgb(255,100,100)",
        "warn",
        "rgb(255,200,90)",
        "rgba(160,180,210,1)",
      ],
      "circle-stroke-width": 1,
      "circle-stroke-opacity": 0.6,
    },
  });
  map.addLayer({
    id: "notam-dot",
    type: "circle",
    source: "notams",
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 2.5, 12, 4, 16, 6],
      "circle-color": [
        "match",
        ["get", "severity"],
        "crit",
        "rgb(255,100,100)",
        "warn",
        "rgb(255,200,90)",
        "rgba(200,210,230,1)",
      ],
      "circle-stroke-color": "rgba(0,0,0,0.6)",
      "circle-stroke-width": 1,
    },
  });

  // Click handler: highlight in the NOTAM panel. The panel listens for
  // pifeeder:focus-notam and scrolls that row into view.
  map.on("click", "notam-dot", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const props = f.properties as { id?: string; code?: string; body?: string };
    document.dispatchEvent(new CustomEvent("pifeeder:show-notams"));
    if (props.id) {
      document.dispatchEvent(new CustomEvent("pifeeder:focus-notam", { detail: { id: props.id } }));
    }
    // Lightweight popup with the first line of the body so users can
    // identify the marker without opening the side panel.
    // escapeHtml on every NOTAM field — previously props.body was passed
    // through `.replace(/[<>&]/g, "")` which DELETES the angle brackets
    // without escaping, so `<script>alert(1)</script>` becomes
    // `scriptalert(1)/script` — still HTML, just stripped of tags. A
    // payload using attribute escapes (`" onmouseover="…"`) walked
    // straight through. escapeHtml replaces with entity codes.
    new maplibregl.Popup({ closeOnClick: true, maxWidth: "300px" })
      .setLngLat(e.lngLat)
      .setHTML(
        `<div style="font-family:var(--font-sans);font-size:12px;line-height:1.4">
           <strong style="color:var(--accent-amber)">${escapeHtml(props.code ?? "NOTAM")}</strong>
           <div style="margin-top:4px;color:#ddd">${escapeHtml(props.body ?? "")}</div>
         </div>`,
      )
      .addTo(map!);
  });
  map.on("mouseenter", "notam-dot", () => {
    if (map) map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "notam-dot", () => {
    if (map) map.getCanvas().style.cursor = "";
  });
}

/**
 * Render the selected aircraft's trail as a polyline coloured by altitude.
 * Each segment between two consecutive trail points carries the average
 * altitude of its endpoints in `properties.alt`, which the line layer's
 * `interpolate` expression maps to the same gradient as the legend bar
 * (see src/altitude-color.ts).
 */
function drawTrail() {
  if (!map || !map.isStyleLoaded()) return;
  const id = $selectedId.get();
  const trail = id ? ($trails.get()[id] ?? []) : [];
  // Build per-segment LineString features so each segment can have its own
  // altitude → color. A single LineString with `line-gradient` would work
  // too, but `line-gradient` requires `lineMetrics: true` on the source AND
  // disallows data-driven `line-color`. The per-segment approach is simpler.
  type TrailFeature = {
    type: "Feature";
    properties: { alt: number };
    geometry: { type: "LineString"; coordinates: [number, number][] };
  };
  const features: TrailFeature[] = [];
  for (let i = 1; i < trail.length; i++) {
    const a = trail[i - 1]!;
    const b = trail[i]!;
    features.push({
      type: "Feature",
      properties: { alt: (a.alt + b.alt) / 2 },
      geometry: {
        type: "LineString",
        coordinates: [
          [a.lon, a.lat],
          [b.lon, b.lat],
        ],
      },
    });
  }
  const data = { type: "FeatureCollection" as const, features };

  if (map.getSource("plane-trail")) {
    (map.getSource("plane-trail") as maplibregl.GeoJSONSource).setData(data as never);
    return;
  }
  map.addSource("plane-trail", { type: "geojson", data: data as never });
  map.addLayer({
    id: "plane-trail-line",
    type: "line",
    source: "plane-trail",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-width": 3,
      "line-color": ["interpolate", ["linear"], ["get", "alt"], ...altitudeStopsForMapLibre()] as never,
      "line-opacity": 0.85,
    },
  });
}

function drawRunways() {
  if (!map) return;
  const lines: GeoJSON.Feature[] = [];
  // Point features at each runway end, carrying the ident (e.g. "24L") so
  // a separate symbol layer can render the label slightly offset from the
  // threshold. Two ends per runway because each one gets its own label.
  const labels: GeoJSON.Feature[] = [];
  for (const ap of Object.values(runwaysData as Record<string, unknown>)) {
    if (typeof ap !== "object" || ap === null) continue;
    const a = ap as {
      runways?: Array<{
        le_lat: number;
        le_lon: number;
        he_lat: number;
        he_lon: number;
        le_ident?: string;
        he_ident?: string;
        name: string;
      }>;
    };
    if (!a.runways) continue;
    for (const r of a.runways) {
      // A runway pair is "active" if any of its ends appears in the
      // multi-runway active set (HND uses multiple in parallel). Fall
      // back to the legacy single activeArr/activeDep check when the
      // API didn't return `activeEnds`.
      const ap = $airport.get();
      const active =
        ap?.activeEnds?.some((e) => e.runway === r.name) ??
        (ap?.activeArr === r.name || ap?.activeDep === r.name);
      lines.push({
        type: "Feature",
        properties: { name: r.name, active },
        geometry: {
          type: "LineString",
          coordinates: [
            [r.le_lon, r.le_lat],
            [r.he_lon, r.he_lat],
          ],
        },
      });
      if (r.le_ident) {
        labels.push({
          type: "Feature",
          properties: { ident: r.le_ident, active },
          geometry: { type: "Point", coordinates: [r.le_lon, r.le_lat] },
        });
      }
      if (r.he_ident) {
        labels.push({
          type: "Feature",
          properties: { ident: r.he_ident, active },
          geometry: { type: "Point", coordinates: [r.he_lon, r.he_lat] },
        });
      }
    }
  }
  const lineCollection: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: lines };
  const labelCollection: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: labels,
  };
  if (map.getSource("runways")) {
    (map.getSource("runways") as maplibregl.GeoJSONSource).setData(lineCollection);
    (map.getSource("runway-ends") as maplibregl.GeoJSONSource).setData(labelCollection);
  } else {
    map.addSource("runways", { type: "geojson", data: lineCollection });
    map.addSource("runway-ends", { type: "geojson", data: labelCollection });
    // Outer runway "pavement" — solid line that mimics asphalt, wide
    // enough at every zoom step that the runway is recognisable as a
    // physical strip (not just a thin annotation). Width grows
    // exponentially with zoom: ~3px at z=10, ~10px at z=12, ~28px at
    // z=14, ~70px at z=16 so the runway visibly fills its real footprint
    // at airport-detail zooms while staying compact when you're zoomed out.
    map.addLayer({
      id: "runways-pavement",
      type: "line",
      source: "runways",
      layout: { "line-cap": "butt", "line-join": "round" },
      paint: {
        "line-color": ["case", ["get", "active"], "rgb(96,72,48)", "rgb(78,82,90)"],
        "line-width": ["interpolate", ["exponential", 1.5], ["zoom"], 8, 1.6, 10, 3, 12, 10, 14, 28, 16, 70],
        "line-opacity": 0.95,
      },
    });
    // Bright outline tracing the pavement edge — gives the runway a clean
    // border and lets active runways glow amber against the asphalt.
    map.addLayer({
      id: "runways-edge",
      type: "line",
      source: "runways",
      paint: {
        "line-color": ["case", ["get", "active"], "rgb(255,208,136)", "rgba(220,225,235,0.6)"],
        "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.6, 12, 1.4, 16, 2.4],
        "line-gap-width": [
          "interpolate",
          ["exponential", 1.5],
          ["zoom"],
          8,
          1.6,
          10,
          3,
          12,
          10,
          14,
          28,
          16,
          70,
        ],
      },
    });
    // Dashed centerline (the painted runway markings).
    map.addLayer({
      id: "runways-centerline",
      type: "line",
      source: "runways",
      minzoom: 11,
      paint: {
        "line-color": "rgba(255,255,255,0.85)",
        "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.8, 14, 1.6, 16, 2.4],
        "line-dasharray": [4, 3],
      },
    });
    // Runway end identifiers ("24L" etc). Hidden at low zoom so they don't
    // clutter the regional view; visible from ~11 onward where individual
    // thresholds become distinguishable.
    map.addLayer({
      id: "runway-end-labels",
      type: "symbol",
      source: "runway-ends",
      minzoom: 11,
      layout: {
        "text-field": ["get", "ident"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 11, 9, 14, 12, 17, 16],
        "text-font": ["Noto Sans Bold"],
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-offset": [0, -0.9],
      },
      paint: {
        "text-color": ["case", ["get", "active"], "rgb(255,208,136)", "rgba(255,255,255,0.9)"],
        "text-halo-color": "rgba(0,0,0,0.85)",
        "text-halo-width": 1.4,
      },
    });
  }
}

function drawAirportMarkers() {
  for (const m0 of airportMarkers) m0.remove();
  airportMarkers.length = 0;
  if (!map) return;
  for (const ap of Object.values(runwaysData as Record<string, unknown>)) {
    if (typeof ap !== "object" || ap === null) continue;
    const a = ap as { lat: number; lon: number; iata: string; icao: string };
    const el = document.createElement("button");
    el.type = "button";
    el.className = "ap-pin";
    el.setAttribute("aria-label", `${a.iata} ${a.icao}`);
    el.innerHTML = `<span class="ap-pin-dot"></span><span class="ap-pin-lbl">${a.iata}</span>`;
    el.addEventListener("click", () => {
      // Switching context triggers the hydrate() subscription in main.ts,
      // which refetches flights/weather/notams for this airport. The map
      // ease is just a UI nicety on top of that.
      $icaoUserPicked.set(true);
      if ($currentIcao.get() !== a.icao) $currentIcao.set(a.icao);
      // Always announce the tap (even if the user re-tapped the already-
      // focused airport) so the mobile sheet can switch to the Airport
      // tab. $currentIcao.subscribe wouldn't fire on a no-op set.
      document.dispatchEvent(new CustomEvent("pifeeder:airport-tap", { detail: { icao: a.icao } }));
      map?.easeTo({ center: [a.lon, a.lat], duration: 700, zoom: Math.max(map.getZoom(), 12) });
    });
    airportMarkers.push(
      new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([a.lon, a.lat]).addTo(map),
    );
  }
}

function drawStationMarker() {
  if (!map) return;
  const s = $station.get();
  // `default` source = the baked-in Setagaya fallback used before we hear
  // from the Pi or the browser geolocation. Rendering a marker there
  // misleads users into thinking their device is in Setagaya — especially
  // when offline / mock mode keeps that default forever. Only show the
  // marker once we have a real coordinate.
  if (stationMarker) {
    stationMarker.remove();
    stationMarker = null;
  }
  if (s.source === "default") return;
  const el = document.createElement("div");
  el.className = "station-pin";
  el.innerHTML = `<span class="station-ring"></span><span class="station-dot"></span><span class="station-lbl">${s.source === "geo" ? "YOU" : "STATION"}</span>`;
  stationMarker = new maplibregl.Marker({ element: el, anchor: "center" })
    .setLngLat([s.lon, s.lat])
    .addTo(map);
}

function syncPlanes() {
  if (!map) return;
  const planes = $planes.get();
  const seen = new Set<string>();
  for (const p of Object.values(planes)) {
    seen.add(p.id);
    let el = planeEls.get(p.id);
    let mk = planeMarkers.get(p.id);
    if (!el || !mk) {
      el = document.createElement("div");
      el.className = "plane";
      el.innerHTML = `<i class="ph-fill ph-airplane"></i><span class="plane-label"></span>`;
      el.addEventListener("click", () => selectPlane(p.id));
      mk = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([p.lon, p.lat]).addTo(map);
      planeEls.set(p.id, el);
      planeMarkers.set(p.id, mk);
    } else {
      mk.setLngLat([p.lon, p.lat]);
    }
    const sel = $selectedId.get() === p.id;
    el.classList.toggle("selected", sel);
    const icon = el.querySelector("i") as HTMLElement;
    icon.style.transform = `rotate(${p.hdg}deg)`;
    const label = el.querySelector(".plane-label") as HTMLElement;
    label.textContent = p.call;
  }
  for (const id of Array.from(planeEls.keys())) {
    if (!seen.has(id)) {
      planeMarkers.get(id)?.remove();
      planeEls.delete(id);
      planeMarkers.delete(id);
    }
  }
}

function updateSelectedClass() {
  const selId = $selectedId.get();
  for (const [id, el] of planeEls) el.classList.toggle("selected", id === selId);
  const mk = selId ? planeMarkers.get(selId) : null;
  if (mk && map) map.easeTo({ center: mk.getLngLat(), duration: 700, zoom: Math.max(map.getZoom(), 9) });
}
